import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Upload, Plus, X, Trash2, Loader2, Inbox, FileText, Image as ImageIcon,
  Lock, RotateCcw, CheckCircle2, AlertTriangle, ExternalLink, Receipt,
  RotateCw,
} from 'lucide-react';
import { gorselKucult, boyutYaz } from '@/lib/gorselKucult';
import MakbuzIsle from '@/components/MakbuzIsle';

/**
 * DOSYA KUTUSU — işlenmeyi bekleyen belgelerin kuyruğu.
 *
 * Ön büro belgeyi atar, muhasebe fatura okuma ekranından seçip işler, kayıt
 * tamamlanınca belge "işlendi"ye düşer. Kutunun boşalması görevin bitmesidir.
 */
const TURLER = [
  ['belirsiz', 'Belirsiz'],
  // 'fatura' ALIŞ faturasıdır (anahtar eski kayıtlar için değişmedi);
  // satış faturası ayrı tür — kutudan satış ekranına gider (2026-10-01).
  ['fatura', 'Alış Faturası'],
  ['satis_faturasi', 'Satış Faturası'],
  ['tahsilat_makbuzu', 'Tahsilat Makbuzu'],
  ['siparis_formu', 'Sipariş Formu'],
  // İrsaliye ve mal kabul formu TEK tür: ikisi de "mal teslim edildi" belgesi
  // ve aynı ekrana (Mal Kabul) gidiyor. Eski kayıtlar sunucuda bu türe okunur.
  ['teslimat_belgesi', 'Teslimat Belgesi (İrsaliye / Fiş)'],
  ['diger', 'Diğer'],
];

// Belge türü → işleneceği ekran. Listede olmayan türde (belirsiz, diğer,
// makbuz…) "İşle" hedefi SORAR.
const ISLEME_HEDEFLERI = {
  fatura: { adres: '/erp/invoices/new', etiket: 'Alış Faturasına İşle' },
  satis_faturasi: { adres: '/erp/satis-faturalari/yeni', etiket: 'Satış Faturasına İşle' },
  teslimat_belgesi: { adres: '/erp/mal-kabul/yeni', etiket: 'Mal Kabule İşle' },
  // Tahsilat makbuzu YENİ KAYIT AÇMAZ: mevcut bir ödeme hareketine ek olur
  // (kullanıcı, 2026-10-03) — ekran değil pencere (`MakbuzIsle`).
  tahsilat_makbuzu: { makbuz: true, etiket: 'Ödemeye Ekle' },
};
const isleEtiketi = (tur) => ISLEME_HEDEFLERI[tur]?.etiket || 'İşle…';

// İşlenen kaydın bağlantısı KAYDIN TÜRÜNE göre. Bir dönem hepsi alış
// faturasına gidiyordu; Mal Kabul'e işlenmiş belgede bağlantı boşa düşüyordu.
const kayitAdresi = (k) => (k.tur === 'odeme' ? (k.cari_id ? `/erp/suppliers/${k.cari_id}` : '/erp/treasury')
  : k.tur === 'mal_kabul' ? `/erp/mal-kabul/${k.id}/duzenle`
  : k.tur === 'satis_faturasi' ? `/erp/satis-faturalari/${k.id}/duzenle`
    : `/erp/invoices/${k.id}`);

const DURUM_RENGI = {
  bekliyor: 'bg-[#7a6a2e]/10 text-[#7a6a2e]',
  isleniyor: 'bg-[#2e2d29]/10 text-[#2e2d29]',
  duzeltme: 'bg-[#7a3535]/10 text-[#7a3535]',
  islendi: 'bg-[#3d6e4e]/10 text-[#3d6e4e]',
};

const zaman = (iso) => {
  const d = new Date(iso);
  return isNaN(d) ? '-' : d.toLocaleString('tr-TR',
    { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

export default function DosyaKutusu() {
  const navigate = useNavigate();
  const { user, hasPermission } = useAuth();
  // Silme `delete` ister (sunucu: DELETE -> delete, 2026-09-28); yetkisi
  // olmayana 403'e götüren düğme gösterilmez.
  const silmeYetkisi = hasPermission('dosya_kutusu', 'delete');
  const [sekme, setSekme] = useState('bekliyor');
  const [belgeler, setBelgeler] = useState([]);
  const [sayilar, setSayilar] = useState({});
  const [yukleniyor, setYukleniyor] = useState(false);
  // Süzgeçler. Tarihler YEREL gün olarak gönderilir; sunucu UTC sınırına çevirir
  // (akşam 22:00'den sonra yüklenen belge ertesi güne düşmesin diye).
  const BOS_SUZGEC = { tur: '', yukleyen: '', tarih_bas: '', tarih_son: '' };
  const [suzgec, setSuzgec] = useState(BOS_SUZGEC);
  const suzgecVar = Object.values(suzgec).some(Boolean);

  const [yuklemeAcik, setYuklemeAcik] = useState(false);
  const [redBelge, setRedBelge] = useState(null);
  const [redGerekce, setRedGerekce] = useState('');
  const [silinecek, setSilinecek] = useState(null);
  const [onizleme, setOnizleme] = useState(null);   // { belge, sira }

  const getir = useCallback(async () => {
    setYukleniyor(true);
    try {
      // Boş süzgeçler gönderilmez; sunucuda gereksiz koşul oluşmasın
      const params = { durum: sekme };
      Object.entries(suzgec).forEach(([k, v]) => { if (v) params[k] = v; });
      const [liste, adet] = await Promise.all([
        api.get('/dosya-kutusu', { params }),
        api.get('/dosya-kutusu/sayilar'),
      ]);
      setBelgeler(liste.data || []);
      setSayilar(adet.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dosya kutusu yüklenemedi');
    } finally {
      setYukleniyor(false);
    }
  }, [sekme, suzgec]);

  useEffect(() => { getir(); }, [getir]);

  const islem = async (calistir, basarili) => {
    try {
      await calistir();
      toast.success(basarili);
      getir();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'İşlem yapılamadı');
    }
  };

  // Belgeyi fatura giris ekranina goturur. Kilit ve okuma orada yapilir;
  // burada yalnizca yonlendiriyoruz ki tek bir yerde kalsin.
  // Kilit BASKASINDAYSA engellenir. Kilit KENDISINDEYSE devam edebilmeli:
  // yarida sayfa degistiren kullanici belgeye geri donemiyordu, tek cikis
  // "Bırak" idi. Sunucu zaten ayni kullanicinin kilidini yenilemeye izin
  // veriyor (bkz. belgeyi_kilitle), engel yalnizca buradaydi.
  // Belge TÜRÜ hedefi belirler: teslimat belgesi (irsaliye / fiş) Mal Kabul
  // ekranına, gerisi fatura ekranına gider. İkisi de aynı AI okumasını
  // kullanıyor, fark kaydın nereye yazıldığı.
  //
  // SATIŞ FATURASI de kutuya düşebiliyor (kullanıcı, 2026-10-01): bir dönem
  // teslimat dışındaki HER belge alış faturası ekranına gidiyordu. Türü
  // belli olmayan belgede (belirsiz, diğer…) hedef SORULUR ve seçim belgenin
  // türüne yazılır — yarıda bırakılan belge "Devam Et"te aynı ekrana döner.
  const [hedefSor, setHedefSor] = useState(null);   // hedefi sorulan belge
  const [makbuzBelge, setMakbuzBelge] = useState(null);   // ödemeye eklenecek makbuz

  const belgeyiIsle = (belge) => {
    if (belge.kilit && belge.kilit.user_id !== user?.id) {
      toast.error(`Bu belge ${belge.kilit.user_adi} tarafından işleniyor`);
      return;
    }
    const hedef = ISLEME_HEDEFLERI[belge.tur];
    if (!hedef) { setHedefSor(belge); return; }
    if (hedef.makbuz) { setMakbuzBelge(belge); return; }
    navigate(`${hedef.adres}?kutu_belge=${belge.id}`);
  };

  const hedefSec = async (tur) => {
    const belge = hedefSor;
    setHedefSor(null);
    try {
      await api.patch(`/dosya-kutusu/${belge.id}`, { tur });
    } catch { /* tür yazılamasa da işlemeye devam edilebilir */ }
    if (ISLEME_HEDEFLERI[tur].makbuz) { setMakbuzBelge({ ...belge, tur }); return; }
    navigate(`${ISLEME_HEDEFLERI[tur].adres}?kutu_belge=${belge.id}`);
  };
  // Eski ad, çağrı yerleri bozulmasın diye korunuyor.
  const faturayaIsle = belgeyiIsle;

  const reddet = async () => {
    if (!redGerekce.trim()) { toast.error('Gerekçe yazmalısınız'); return; }
    await islem(() => api.post(`/dosya-kutusu/${redBelge.id}/reddet`,
      { gerekce: redGerekce.trim() }), 'Belge düzeltmeye gönderildi');
    setRedBelge(null);
    setRedGerekce('');
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="dosya-kutusu-sayfasi">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-[#1a1916] flex items-center gap-2">
            <Inbox size={22} /> Dosya Kutusu
          </h1>
          <p className="text-sm text-[#8a7e70] mt-0.5">
            Belgeleri buraya atın; işlendikçe kutudan düşer.
          </p>
        </div>
        <Button onClick={() => setYuklemeAcik(true)}
          className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] h-11 md:h-9 font-semibold"
          data-testid="belge-yukle-btn">
          <Upload size={16} className="mr-2" /> Belge Yükle
        </Button>
      </div>

      <Tabs value={sekme} onValueChange={setSekme}>
        <TabsList className="flex-wrap h-auto">
          {[['bekliyor', 'Bekleyen'], ['isleniyor', 'İşleniyor'],
            ['duzeltme', 'Düzeltme Bekliyor'], ['islendi', 'İşlendi']].map(([k, ad]) => (
            <TabsTrigger key={k} value={k} data-testid={`sekme-${k}`}>
              {ad}
              {sayilar[k] > 0 && (
                <Badge className={`ml-2 ${DURUM_RENGI[k]}`}>{sayilar[k]}</Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Süzgeçler */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 bg-[#faf7f2] border border-[#d0c4b0]
                      rounded-lg p-2.5" data-testid="kutu-suzgecleri">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold text-[#8a7e70] uppercase mb-1">Belge Türü</p>
          <select value={suzgec.tur}
            onChange={(e) => setSuzgec(p => ({ ...p, tur: e.target.value }))}
            className="w-full h-11 md:h-9 rounded-md border border-[#d0c4b0] bg-white px-2
                       text-base md:text-sm min-w-0"
            data-testid="suzgec-tur">
            <option value="">Tüm türler</option>
            {TURLER.map(([k, ad]) => <option key={k} value={k}>{ad}</option>)}
          </select>
        </div>

        <div className="min-w-0">
          <p className="text-[10px] font-semibold text-[#8a7e70] uppercase mb-1">Yükleyen</p>
          <select value={suzgec.yukleyen}
            onChange={(e) => setSuzgec(p => ({ ...p, yukleyen: e.target.value }))}
            className="w-full h-11 md:h-9 rounded-md border border-[#d0c4b0] bg-white px-2
                       text-base md:text-sm min-w-0"
            data-testid="suzgec-yukleyen">
            <option value="">Herkes</option>
            {/* Yalnızca gerçekten belge yüklemiş kişiler listelenir */}
            {(sayilar.yukleyenler || []).map(u => (
              <option key={u.id} value={u.id}>{u.ad} ({u.adet})</option>
            ))}
          </select>
        </div>

        <div className="min-w-0">
          <p className="text-[10px] font-semibold text-[#8a7e70] uppercase mb-1">Başlangıç</p>
          <Input type="date" value={suzgec.tarih_bas}
            onChange={(e) => setSuzgec(p => ({ ...p, tarih_bas: e.target.value }))}
            className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0"
            data-testid="suzgec-tarih-bas" />
        </div>

        <div className="min-w-0">
          <p className="text-[10px] font-semibold text-[#8a7e70] uppercase mb-1">Bitiş</p>
          <div className="flex gap-1">
            <Input type="date" value={suzgec.tarih_son}
              onChange={(e) => setSuzgec(p => ({ ...p, tarih_son: e.target.value }))}
              className="bg-white border-[#d0c4b0] h-11 md:h-9 text-base md:text-sm w-full min-w-0"
              data-testid="suzgec-tarih-son" />
            {suzgecVar && (
              <Button variant="ghost" onClick={() => setSuzgec(BOS_SUZGEC)}
                title="Süzgeçleri temizle"
                className="h-11 md:h-9 w-11 md:w-9 p-0 shrink-0 text-[#8a7e70] hover:text-[#7a3535]"
                data-testid="suzgec-temizle">
                <X size={16} />
              </Button>
            )}
          </div>
        </div>
      </div>

      {yukleniyor ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin" size={24} /></div>
      ) : belgeler.length === 0 ? (
        <div className="text-center py-16 text-[#8a7e70] bg-[#faf7f2] border border-[#d0c4b0] rounded-lg">
          <Inbox size={44} className="mx-auto mb-3 opacity-40" />
          {/* Süzgeç yüzünden boşsa "kutu boş" demek yanıltıcı olur */}
          {suzgecVar ? (
            <>
              <p className="font-medium">Süzgece uyan belge yok</p>
              <button type="button" onClick={() => setSuzgec(BOS_SUZGEC)}
                className="text-xs text-[#3d6e4e] hover:underline mt-1">
                Süzgeçleri temizle
              </button>
            </>
          ) : (
            <>
              <p className="font-medium">
                {sekme === 'bekliyor' ? 'Kutu boş — işlenecek belge yok' : 'Bu durumda belge yok'}
              </p>
              {sekme === 'bekliyor' && (
                <p className="text-xs mt-1">Görev tamam. Yeni belge geldiğinde burada görünecek.</p>
              )}
            </>
          )}
        </div>
      ) : (
        <>
        {suzgecVar && (
          <p className="text-xs text-[#8a7e70]" data-testid="suzgec-sonuc">
            {belgeler.length} belge listeleniyor
          </p>
        )}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {belgeler.map(b => (
            <BelgeKarti key={b.id} belge={b} onIslem={islem} onGetir={getir}
              benimKilidim={Boolean(b.kilit) && b.kilit.user_id === user?.id}
              onReddet={() => { setRedBelge(b); setRedGerekce(''); }}
              onSil={silmeYetkisi ? () => setSilinecek(b) : null}
              onIsle={() => faturayaIsle(b)}
              onOnizle={(sira) => setOnizleme({ belge: b, sira })} />
          ))}
        </div>
        </>
      )}

      <YuklemeDialogu acik={yuklemeAcik} kapat={() => setYuklemeAcik(false)} onBitti={getir} />

      {/* Red gerekçesi */}
      <Dialog open={Boolean(redBelge)} onOpenChange={(a) => !a && setRedBelge(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916]">Belgeyi düzeltmeye gönder</DialogTitle>
            <DialogDescription className="text-[#8a7e70]">
              Belge silinmez; yükleyen kişi gerekçeyi görüp yeniden çekebilir.
            </DialogDescription>
          </DialogHeader>
          <Textarea value={redGerekce} onChange={(e) => setRedGerekce(e.target.value)}
            placeholder="Örn: Fotoğraf bulanık, tutar okunmuyor — tekrar çekin"
            className="bg-white border-[#d0c4b0] min-h-24" data-testid="red-gerekce" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRedBelge(null)}
              className="border-[#d0c4b0]">Vazgeç</Button>
            <Button onClick={reddet} className="bg-[#7a3535] text-white hover:bg-[#5e2929]"
              data-testid="red-onayla">Düzeltmeye Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Silme onayı */}
      <AlertDialog open={Boolean(silinecek)} onOpenChange={(a) => !a && setSilinecek(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Belge silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              {silinecek && `${silinecek.sayfa_sayisi} sayfalık belge ve dosyaları kalıcı olarak silinecek. Bu işlem geri alınamaz.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="bg-[#7a3535] hover:bg-[#5e2929]"
              data-testid="silme-onayla"
              onClick={() => { islem(() => api.delete(`/dosya-kutusu/${silinecek.id}`), 'Belge silindi'); setSilinecek(null); }}>
              Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sayfa önizleme */}
      <Dialog open={Boolean(hedefSor)} onOpenChange={(a) => !a && setHedefSor(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-sm" data-testid="hedef-sor">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Bu belge nereye işlensin?</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#8a7e70] -mt-2">
            Seçiminiz belgenin türüne de yazılır; yarıda bırakırsanız "Devam Et" aynı ekrana döner.
          </p>
          <div className="grid gap-2">
            {[['fatura', 'Alış Faturası', 'Tedarikçiden aldığımız mal/hizmet'],
              ['satis_faturasi', 'Satış Faturası', 'Bizim kestiğimiz ya da firma iade faturası'],
              ['teslimat_belgesi', 'Mal Kabul', 'İrsaliye / teslim fişi'],
              ['tahsilat_makbuzu', 'Tahsilat Makbuzu', 'Yaptığımız bir ödemenin makbuzu — ödemeye eklenir']].map(([tur, ad, aciklama]) => (
              <Button key={tur} variant="outline" onClick={() => hedefSec(tur)}
                className="border-[#d0c4b0] h-auto py-2.5 justify-start flex-col items-start"
                data-testid={`hedef-${tur}`}>
                <span className="font-semibold text-[#1a1916]">{ad}</span>
                <span className="text-[11px] font-normal text-[#8a7e70]">{aciklama}</span>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {makbuzBelge && (
        <MakbuzIsle belge={makbuzBelge}
          onKapat={() => { setMakbuzBelge(null); getir(); }}
          onIslendi={() => { setMakbuzBelge(null); getir(); }} />
      )}

      <Dialog open={Boolean(onizleme)} onOpenChange={(a) => !a && setOnizleme(null)}>
        <DialogContent className="max-w-4xl bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916]">
              {onizleme && `Sayfa ${onizleme.sira} / ${onizleme.belge.sayfa_sayisi}`}
            </DialogTitle>
          </DialogHeader>
          {onizleme && <SayfaGoruntule belge={onizleme.belge} sira={onizleme.sira}
            onDegis={(s) => setOnizleme({ ...onizleme, sira: s })} />}
          {/* Belgeyi açan kişi kapatmadan doğrudan işlemeye geçebilsin */}
          {onizleme && onizleme.belge.durum !== 'islendi' && !onizleme.belge.kilit && (
            <DialogFooter>
              <Button onClick={() => { const b = onizleme.belge; setOnizleme(null); faturayaIsle(b); }}
                className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] h-11 md:h-9"
                data-testid="onizleme-faturaya-isle">
                <Receipt size={15} className="mr-2" />
                {isleEtiketi(onizleme.belge.tur)}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Tek belgenin kartı */
function BelgeKarti({ belge, onIslem, onGetir, onReddet, onSil, onOnizle, onIsle, benimKilidim }) {
  const sayfaEkleRef = useRef(null);
  const [ekleniyor, setEkleniyor] = useState(false);

  const sayfaEkle = async (e) => {
    const dosyalar = [...(e.target.files || [])];
    if (!dosyalar.length) return;
    setEkleniyor(true);
    try {
      const fd = new FormData();
      for (const d of dosyalar) fd.append('dosyalar', await gorselKucult(d));
      const r = await api.post(`/dosya-kutusu/${belge.id}/sayfa`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${r.data.eklenen} sayfa eklendi (toplam ${r.data.toplam_sayfa})`);
      onGetir();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sayfa eklenemedi');
    } finally {
      setEkleniyor(false);
      if (sayfaEkleRef.current) sayfaEkleRef.current.value = '';
    }
  };

  const kapali = belge.durum === 'islendi';

  return (
    <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg p-3 flex flex-col gap-2"
      data-testid={`belge-${belge.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="font-semibold text-[#1a1916] truncate">{belge.tur_adi}</p>
          <p className="text-xs text-[#8a7e70]">
            {belge.yukleyen_adi} · {zaman(belge.yuklendi_at)}
          </p>
        </div>
        <Badge className={DURUM_RENGI[belge.durum]}>{belge.durum_adi}</Badge>
      </div>

      {/* Sayfa küçük görselleri */}
      <div className="flex gap-1.5 flex-wrap">
        {(belge.sayfalar || []).map(s => (
          <button key={s.sira} type="button" onClick={() => onOnizle(s.sira)}
            title={`${s.dosya_adi} · ${boyutYaz(s.boyut)}`}
            className="h-14 w-14 rounded border border-[#d0c4b0] bg-white flex items-center
                       justify-center text-[#8a7e70] hover:border-[#3d6e4e] transition-colors"
            data-testid={`sayfa-${belge.id}-${s.sira}`}>
            {(s.content_type || '').includes('pdf')
              ? <FileText size={18} />
              : <ImageIcon size={18} />}
            <span className="absolute mt-9 text-[9px] font-bold">{s.sira}</span>
          </button>
        ))}
        {!kapali && (
          <>
            <button type="button" onClick={() => sayfaEkleRef.current?.click()}
              disabled={ekleniyor}
              className="h-14 w-14 rounded border border-dashed border-[#3d6e4e] text-[#3d6e4e]
                         flex flex-col items-center justify-center hover:bg-[#3d6e4e]/5"
              title="Bu belgeye sayfa ekle"
              data-testid={`sayfa-ekle-${belge.id}`}>
              {ekleniyor ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
              <span className="text-[9px] mt-0.5">sayfa</span>
            </button>
            <input ref={sayfaEkleRef} type="file" multiple className="hidden"
              accept="image/*,application/pdf" onChange={sayfaEkle} />
          </>
        )}
      </div>

      {belge.notlar && <p className="text-xs text-[#3F3E39] italic">{belge.notlar}</p>}

      {belge.red_notu && (
        <div className="text-xs bg-[#7a3535]/5 border border-[#7a3535]/30 rounded p-2">
          <p className="font-semibold text-[#7a3535] flex items-center gap-1">
            <AlertTriangle size={12} /> Düzeltme isteniyor
          </p>
          <p className="text-[#3F3E39] mt-0.5">{belge.red_notu.gerekce}</p>
          <p className="text-[#8a7e70] mt-0.5">
            {belge.red_notu.user_adi} · {zaman(belge.red_notu.at)}
          </p>
        </div>
      )}

      {belge.kilit && belge.durum === 'isleniyor' && (
        <p className="text-xs text-[#8a7e70] flex items-center gap-1">
          <Lock size={11} />
          {benimKilidim ? 'Siz işliyorsunuz — yarım kaldı' : `${belge.kilit.user_adi} işliyor`}
        </p>
      )}

      {belge.islenen_kayit && (
        <a href={kayitAdresi(belge.islenen_kayit)}
          className="text-xs text-[#3d6e4e] hover:underline flex items-center gap-1"
          data-testid={`kayit-bagi-${belge.id}`}>
          <CheckCircle2 size={12} />
          {belge.islenen_kayit.no || 'Kayda işlendi'} — {belge.islenen_kayit.user_adi}
          <ExternalLink size={10} />
        </a>
      )}

      {/* İşlemler */}
      <div className="flex flex-wrap gap-1 mt-auto pt-1">
        {/* Faturaya işle: türü fatura ya da belirsiz olanlarda BIRINCIL.
            Diğer türlerde de açık tutuluyor — yanlış etiketlenmiş bir belge
            çıkmaz sokak olmasın. İşlenmişte gizli.
            Kilit BASKASINDAYSA gizli, KENDISINDEYSE "Devam Et" olur: yarıda
            bırakan kullanıcı belgeye dönebilsin. Eskiden tek çıkış "Bırak"tı
            ve kilidin sahibi de dışarıda kalıyordu. */}
        {!kapali && (!belge.kilit || benimKilidim) && (
          <Button size="sm" onClick={onIsle}
            className={['fatura', 'satis_faturasi', 'belirsiz', 'teslimat_belgesi', 'tahsilat_makbuzu'].includes(belge.tur) || benimKilidim
              ? 'bg-[#3d6e4e] text-white hover:bg-[#2e5a3e] text-xs h-9'
              : 'bg-transparent text-[#3d6e4e] hover:bg-[#3d6e4e]/10 text-xs h-9 border border-[#3d6e4e]/40'}
            data-testid={`faturaya-isle-${belge.id}`}>
            <Receipt size={13} className="mr-1" />
            {benimKilidim ? 'Devam Et' : isleEtiketi(belge.tur)}
          </Button>
        )}
        {belge.durum === 'isleniyor' && (
          <Button size="sm" variant="ghost" className="text-[#8a7e70] text-xs"
            onClick={() => onIslem(() => api.post(`/dosya-kutusu/${belge.id}/birak`),
              'Belge kutuya geri bırakıldı')}
            data-testid={`birak-${belge.id}`}>
            <RotateCcw size={13} className="mr-1" /> Bırak
          </Button>
        )}
        {!kapali && (
          <Button size="sm" variant="ghost" className="text-[#7a3535] text-xs"
            onClick={onReddet} data-testid={`reddet-${belge.id}`}>
            <AlertTriangle size={13} className="mr-1" /> Düzeltme İste
          </Button>
        )}
        {onSil && (
        <Button size="sm" variant="ghost" className="text-[#7a3535] text-xs ml-auto"
          onClick={onSil} data-testid={`sil-${belge.id}`}>
          <Trash2 size={13} />
        </Button>
        )}
      </div>
    </div>
  );
}

/** Önizleme: sayfayı gösterir, sayfalar arası gezinir */
function SayfaGoruntule({ belge, sira, onDegis }) {
  const [url, setUrl] = useState(null);
  const [tur, setTur] = useState('');
  const [tazele, setTazele] = useState(0);
  const [donduruluyor, setDonduruluyor] = useState(false);

  useEffect(() => {
    let iptal = false, olusan = null;
    api.get(`/dosya-kutusu/${belge.id}/sayfa/${sira}`, { responseType: 'blob' })
      .then(r => {
        if (iptal) return;
        olusan = URL.createObjectURL(r.data);
        setUrl(olusan);
        setTur(r.data.type || '');
      })
      .catch(() => !iptal && toast.error('Sayfa açılamadı'));
    return () => { iptal = true; if (olusan) URL.revokeObjectURL(olusan); };
  }, [belge.id, sira, tazele]);

  // Döndürme KALICI: açı sunucuda saklanır, görsel GridFS'te olduğu gibi kalır.
  // Böylece bir kez düzeltilen belge herkese düz görünür ve AI okumasına da
  // düzeltilmiş gider. Her döndürmede JPEG yeniden sıkıştırılmıyor.
  const dondur = async (yon) => {
    const sayfa = (belge.sayfalar || []).find(s => s.sira === sira);
    const mevcut = sayfa?.rotasyon || 0;
    const yeni = (mevcut + (yon === 'sag' ? 90 : 270)) % 360;
    setDonduruluyor(true);
    try {
      await api.patch(`/dosya-kutusu/${belge.id}/sayfa/${sira}/rotasyon`,
        { rotasyon: yeni });
      if (sayfa) sayfa.rotasyon = yeni;   // yerel kopya, yeniden yüklemeden
      setTazele(t => t + 1);
    } catch {
      toast.error('Döndürülemedi');
    }
    setDonduruluyor(false);
  };

  return (
    <div className="space-y-2">
      {/* items-start ŞART: flex öğesinin varsayılan hizası stretch, görselin
          boyu kabın 65vh'sine zorlanıp en/boy oranı bozuluyordu. Masaüstünde
          belge 65vh'den uzun olduğu için eziliyor, telefonda sığdığı için
          fark edilmiyordu. */}
      {/* Döndürme — yan çekilmiş belgeler için. PDF'te gizli: PDF döndürme
          ayrı bir iş ve görüntüleyici zaten kendi aracını sunuyor. */}
      {!tur.includes('pdf') && (
        <div className="flex items-center justify-end gap-1">
          <span className="text-[11px] text-[#8a7e70] mr-1">Döndür</span>
          <Button size="sm" variant="outline" disabled={donduruluyor}
            onClick={() => dondur('sol')}
            className="h-7 w-7 p-0 border-[#d0c4b0]" title="Sola döndür"
            data-testid="sayfa-dondur-sol">
            <RotateCcw size={13} />
          </Button>
          <Button size="sm" variant="outline" disabled={donduruluyor}
            onClick={() => dondur('sag')}
            className="h-7 w-7 p-0 border-[#d0c4b0]" title="Sağa döndür"
            data-testid="sayfa-dondur-sag">
            <RotateCw size={13} />
          </Button>
        </div>
      )}
      <div className="bg-white border border-[#d0c4b0] rounded max-h-[65vh] overflow-auto flex items-start justify-center">
        {!url ? <div className="py-16"><Loader2 className="animate-spin" size={22} /></div>
          : tur.includes('pdf')
            ? <iframe src={url} title="Belge" className="w-full h-[65vh]" />
            : <img src={url} alt={`Sayfa ${sira}`} className="max-w-full h-auto" />}
      </div>
      {belge.sayfa_sayisi > 1 && (
        <div className="flex gap-1 justify-center flex-wrap">
          {(belge.sayfalar || []).map(s => (
            <Button key={s.sira} size="sm"
              variant={s.sira === sira ? 'default' : 'outline'}
              className={s.sira === sira ? 'bg-[#2e2d29] text-[#f5f0e8] h-8 w-8 p-0'
                : 'border-[#d0c4b0] h-8 w-8 p-0'}
              onClick={() => onDegis(s.sira)}>
              {s.sira}
            </Button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Yükleme penceresi: çoklu dosya, kamera, kapatmadan sayfa ekleme */
function YuklemeDialogu({ acik, kapat, onBitti }) {
  const [dosyalar, setDosyalar] = useState([]);
  const [tur, setTur] = useState('belirsiz');
  const [notlar, setNotlar] = useState('');
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [mukerrer, setMukerrer] = useState([]);
  const seçRef = useRef(null);

  useEffect(() => {
    if (!acik) { setDosyalar([]); setTur('belirsiz'); setNotlar(''); setMukerrer([]); }
  }, [acik]);

  const ekle = async (e) => {
    const yeni = [...(e.target.files || [])];
    if (!yeni.length) return;
    // Küçültme yüklemeden ÖNCE: 4 MB'lık kare ~300 KB'a iner
    const hazir = [];
    for (const d of yeni) hazir.push(await gorselKucult(d));
    setDosyalar(p => [...p, ...hazir]);
    e.target.value = '';
  };

  const cikar = (i) => setDosyalar(p => p.filter((_, j) => j !== i));

  const gonder = async () => {
    if (!dosyalar.length) { toast.error('En az bir sayfa ekleyin'); return; }
    setGonderiliyor(true);
    try {
      const fd = new FormData();
      dosyalar.forEach(d => fd.append('dosyalar', d));
      fd.append('tur', tur);
      fd.append('notlar', notlar);
      const r = await api.post('/dosya-kutusu', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      const muk = r.data.mukerrer_uyarisi || [];
      if (muk.length) {
        setMukerrer(muk);
        toast.warning('Bu belge daha önce yüklenmiş olabilir', {
          description: `${muk.length} benzer kayıt bulundu — kontrol edin.` });
      } else {
        toast.success(`Belge kutuya eklendi (${dosyalar.length} sayfa)`);
        kapat();
      }
      onBitti();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Yüklenemedi');
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <Dialog open={acik} onOpenChange={(a) => !a && kapat()}>
      <DialogContent className="max-w-lg bg-[#faf7f2] border-[#d0c4b0]">
        <DialogHeader>
          <DialogTitle className="text-[#1a1916]">Belge Yükle</DialogTitle>
          <DialogDescription className="text-[#8a7e70]">
            Birden fazla sayfa tek belge olarak yüklenir. Pencereyi kapatmadan sayfa ekleyin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <p className="text-[10px] font-semibold text-[#8a7e70] uppercase mb-1">
              Belge Türü <span className="normal-case font-normal">(bilmiyorsanız boş bırakın)</span>
            </p>
            <select value={tur} onChange={(e) => setTur(e.target.value)}
              className="w-full h-11 md:h-9 rounded-md border border-[#d0c4b0] bg-white px-2 text-base md:text-sm"
              data-testid="belge-turu">
              {TURLER.map(([k, ad]) => <option key={k} value={k}>{ad}</option>)}
            </select>
          </div>

          {/* Ayrı kamera butonu YOK: iOS/Android `accept="image/*"` gördüğünde
              zaten "Fotoğraf Arşivi / Fotoğraf Çek / Dosyaları Seçin" seçeneklerini
              kendisi sunuyor. `capture` niteliği ise seçenek sunmayıp doğrudan
              kamerayı zorluyor — kullanıcının seçimini elinden alıyordu. */}
          <Button type="button" variant="outline" onClick={() => seçRef.current?.click()}
            className="w-full border-[#d0c4b0] h-11" data-testid="dosya-sec">
            <Upload size={16} className="mr-2" /> Dosya veya Fotoğraf Seç
          </Button>
          <input ref={seçRef} type="file" multiple accept="image/*,application/pdf"
            className="hidden" onChange={ekle} />

          {dosyalar.length > 0 && (
            <div className="border border-[#d0c4b0] rounded-md bg-white divide-y divide-[#e6dcc9]
                            max-h-48 overflow-auto">
              {dosyalar.map((d, i) => (
                <div key={i} className="flex items-center gap-2 p-2 text-sm">
                  <span className="w-6 text-center text-xs font-bold text-[#8a7e70]">{i + 1}</span>
                  {d.type.includes('pdf') ? <FileText size={15} className="text-[#8a7e70]" />
                    : <ImageIcon size={15} className="text-[#8a7e70]" />}
                  <span className="flex-1 truncate">{d.name}</span>
                  <span className="text-xs text-[#8a7e70]">{boyutYaz(d.size)}</span>
                  <button type="button" onClick={() => cikar(i)}
                    className="text-[#8a7e70] hover:text-[#7a3535] p-1"><X size={14} /></button>
                </div>
              ))}
            </div>
          )}

          {mukerrer.length > 0 && (
            <div className="text-xs bg-[#7a6a2e]/5 border border-[#7a6a2e]/30 rounded p-2">
              <p className="font-semibold text-[#7a6a2e] flex items-center gap-1">
                <AlertTriangle size={12} /> Benzer belge bulundu
              </p>
              {mukerrer.map(m => (
                <p key={m.id} className="text-[#3F3E39] mt-0.5">
                  {m.tur} · {m.durum} · {zaman(m.yuklendi_at)} · {m.yukleyen_adi}
                </p>
              ))}
              <p className="text-[#8a7e70] mt-1">Belge yine de kutuya eklendi; gereksizse silin.</p>
            </div>
          )}

          <Input value={notlar} onChange={(e) => setNotlar(e.target.value)}
            placeholder="Not (opsiyonel)"
            className="bg-white border-[#d0c4b0] h-11 md:h-9" data-testid="belge-not" />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={kapat} className="border-[#d0c4b0]">Kapat</Button>
          <Button onClick={gonder} disabled={gonderiliyor || !dosyalar.length}
            className="bg-[#3d6e4e] text-white hover:bg-[#2e5a3e]" data-testid="belge-gonder">
            {gonderiliyor ? <Loader2 size={16} className="mr-2 animate-spin" /> : null}
            {dosyalar.length ? `${dosyalar.length} Sayfayı Yükle` : 'Yükle'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
